import { useTranslation } from 'react-i18next';
import { useDroppable } from '@dnd-kit/core';
import { CalendarClock, Trophy } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BoardCard } from './BoardCard';
import type { BoardColumn } from './board-utils';
import type { RouteSettingBoardCard } from '../../../types/route-setting-board';

interface BoardColumnViewProps {
  column: BoardColumn;
  gymSlug: string | null;
  /** The board's average age, which each card's colour is read against. */
  averageAgeDays?: number | null;
  onCardImageClick: (card: RouteSettingBoardCard) => void;
  onCardConfirm: (card: RouteSettingBoardCard) => void;
  onCardDelete: (card: RouteSettingBoardCard) => void;
}

export function BoardColumnView({
  column,
  gymSlug,
  averageAgeDays = null,
  onCardImageClick,
  onCardConfirm,
  onCardDelete,
}: BoardColumnViewProps) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  // A column with no value for the grouping says which one it is missing: "No
  // zone" and "Ungraded" are not the same absence.
  const title =
    column.label ?? t(`gymAdmin.routeSettingBoard.unassigned.${column.grouping}`);

  return (
    <section
      ref={setNodeRef}
      data-testid="route-setting-column"
      aria-label={title}
      className={[
        // `min-h-0` so the card list below can be the part that scrolls: the
        // column is as tall as the board, its header stays put.
        'flex min-h-0 w-72 shrink-0 flex-col rounded-lg border bg-muted/30 transition-colors',
        isOver ? 'border-primary bg-primary/5' : '',
      ].join(' ')}
    >
      <header className="shrink-0 space-y-2 border-b p-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="min-w-0 truncate font-medium">{title}</h3>
          <Badge variant="secondary" className="shrink-0">
            {column.cards.length}
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span
            className="flex items-center gap-1"
            title={t('gymAdmin.routeSettingBoard.averageAgeHint')}
            data-testid="column-average-age"
          >
            <CalendarClock className="size-3" aria-hidden />
            {column.averageAgeDays === null
              ? '—'
              : t('gymAdmin.routeSettingBoard.days', {
                  count: Math.round(column.averageAgeDays),
                })}
          </span>
          <span
            className="flex items-center gap-1"
            title={t('gymAdmin.routeSettingBoard.sends')}
            data-testid="column-sends"
          >
            <Trophy className="size-3" aria-hidden />
            {column.sendsCount}
          </span>
        </div>
      </header>

      {/* The scrolling part: a long column runs under its own header rather than
          pushing the board down the page.
          No empty state: a column with nothing in it is not drawn at all. */}
      <div
        className="flex min-h-24 flex-1 flex-col gap-2 overflow-y-auto p-3"
        data-testid="column-cards"
      >
        {column.cards.map((card) => (
          <BoardCard
            key={card.id}
            card={card}
            gymSlug={gymSlug}
            averageAgeDays={averageAgeDays}
            onImageClick={onCardImageClick}
            onConfirm={onCardConfirm}
            onDelete={onCardDelete}
          />
        ))}
      </div>
    </section>
  );
}
